import { inject, Injectable } from '@angular/core';
import { AuthService } from '@core/authentication/auth.service';
import {
  catchError,
  combineLatest,
  map,
  merge,
  of,
  shareReplay,
  startWith,
  Subject,
  switchMap,
} from 'rxjs';
import { OiqNotificationEventsStore } from '../events/events.store';
import { OiqSmsAlertsService, OiqSmsAlertsUnreadCount } from './sms-alerts.service';

const NO_UNREAD: OiqSmsAlertsUnreadCount = { count: 0, hasUnread: false };

@Injectable({
  providedIn: 'root',
})
export class OiqSmsAlertsStore {
  constructor(
    private service: OiqSmsAlertsService,
    private eventsStore: OiqNotificationEventsStore
  ) {}

  private authService = inject(AuthService);

  reload$ = new Subject<void>();

  /**
   * Emits on login, logout and login-as. The store is a root singleton, so without this the
   * next user in the same tab sees the previous user's SMS until the next poll.
   */
  private session$ = combineLatest([
    this.authService.isLoggedIn,
    this.authService.isMimicUserLoggedIn,
  ]).pipe(map(([_isLoggedIn]) => _isLoggedIn));

  /**
   * Polled on the same interval as the calendar count, and refreshed after mark-read.
   * Starts over at 0 on login, logout and login-as so a user never sees the previous user's count.
   */
  unreadCount$ = this.session$.pipe(
    switchMap(_isLoggedIn =>
      _isLoggedIn
        ? merge(this.eventsStore.interval$, this.reload$).pipe(
            switchMap(() => this.service.unreadCount()),
            startWith(NO_UNREAD)
          )
        : of(NO_UNREAD)
    ),
    shareReplay(1)
  );

  /**
   * startWith: the SMS tab subscribes after the panel's initial reload$ has already fired.
   * null (the loader) on every session change, so the previous user's replies are never shown.
   */
  alerts$ = this.session$.pipe(
    switchMap(_isLoggedIn =>
      _isLoggedIn
        ? this.reload$.pipe(
            startWith(undefined),
            switchMap(() => this.service.list().pipe(catchError(() => of([])))),
            startWith(null)
          )
        : of(null)
    ),
    shareReplay(1)
  );

  markRead(smsMessageIds: number[]): void {
    if (!smsMessageIds.length) {
      return;
    }
    this.service.markRead(smsMessageIds).subscribe({
      next: () => this.reload$.next(),
    });
  }
}
